import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import axios from 'axios'
import { API_BASE_URL } from '../../utils/api'
import LoadingSpinner from '../LoadingSpinner'
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js'
import { Bar, Doughnut, Line } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
)

interface TeamStats {
  totalMembers: number
  activeCollaborators: number
  departmentCount: number
  recentLoginCount: number
}

interface ActivitySummary {
  actionCounts: Array<{ action: string; count: number }>
  userActivity: Array<{
    first_name: string
    last_name: string
    email: string
    activity_count: number
  }>
  dailyActivity: Array<{ date: string; count: number }>
  totalActivities: number
}

interface Department {
  id: number
  name: string
  color: string
  memberCount: number
  lead_first_name?: string
  lead_last_name?: string
}

interface TeamMember {
  id: number
  first_name: string
  last_name: string
  email: string
  role: string
  collaborator_status?: string
  last_login_at?: string
  login_count?: number
  department_name?: string
  level?: string
}

export const TeamAnalyticsDashboard: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null)
  const [activitySummary, setActivitySummary] = useState<ActivitySummary | null>(null)
  const [departments, setDepartments] = useState<Department[]>([])
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [selectedTimeRange, setSelectedTimeRange] = useState('7') // days
  const [activeView, setActiveView] = useState('overview') // overview, activity, team

  useEffect(() => {
    fetchTeamAnalytics()
  }, [selectedTimeRange])

  const fetchTeamAnalytics = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`${API_BASE_URL}/api/team-management/overview`)
      const data = response.data.data

      setTeamStats(data.stats)
      setActivitySummary(data.activitySummary)
      setDepartments(data.departments)
      setTeamMembers(data.teamMembers)
    } catch (error) {
      console.error('Error fetching team analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  // Prepare chart data
  const activityByActionData = {
    labels: activitySummary?.actionCounts.map(item => item.action.replace('_', ' ').toUpperCase()) || [],
    datasets: [{
      label: 'Activities',
      data: activitySummary?.actionCounts.map(item => item.count) || [],
      backgroundColor: [
        '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
        '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
      ],
      borderWidth: 0,
    }]
  }

  const userActivityData = {
    labels: activitySummary?.userActivity.slice(0, 10).map(user => 
      `${user.first_name} ${user.last_name}`) || [],
    datasets: [{
      label: 'Activities',
      data: activitySummary?.userActivity.slice(0, 10).map(user => user.activity_count) || [],
      backgroundColor: '#3B82F6',
      borderColor: '#2563EB',
      borderWidth: 1,
    }]
  }

  const dailyActivityData = {
    labels: activitySummary?.dailyActivity.map(day => 
      new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })) || [],
    datasets: [{
      label: 'Daily Activities',
      data: activitySummary?.dailyActivity.map(day => day.count) || [],
      borderColor: '#10B981',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      tension: 0.1,
      fill: true,
    }]
  }

  const departmentDistributionData = {
    labels: departments.map(dept => dept.name),
    datasets: [{
      data: departments.map(dept => dept.memberCount),
      backgroundColor: departments.map(dept => dept.color),
      borderWidth: 0,
    }]
  }

  const levelDistribution = teamMembers.reduce((acc, member) => {
    const level = member.level || 'unassigned'
    acc[level] = (acc[level] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const levelDistributionData = {
    labels: Object.keys(levelDistribution),
    datasets: [{
      data: Object.values(levelDistribution),
      backgroundColor: ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'],
      borderWidth: 0,
    }]
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (!teamStats && !activitySummary && departments.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Analytics Data</h3>
        <p className="text-gray-500 dark:text-gray-400">Team activity data will appear here once you have collaborators.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Navigation */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Team Analytics</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Insights and performance metrics for your team
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', name: '📊 Overview', desc: 'Key metrics' },
            { id: 'activity', name: '⚡ Activity', desc: 'Team activity trends' },
            { id: 'team', name: '👥 Team Structure', desc: 'Departments & hierarchy' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeView === tab.id
                  ? 'border-gray-900 text-gray-900 dark:text-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="text-center">
                <div>{tab.name}</div>
                <div className="text-xs text-gray-400 mt-1">{tab.desc}</div>
              </div>
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeView === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-900 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Members</h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{teamStats?.totalMembers || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Collaborators</h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{teamStats?.activeCollaborators || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-900 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h1m-1-4h1m4 4h1m-1-4h1" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Departments</h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{teamStats?.departmentCount || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Activity</h3>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{activitySummary?.totalActivities || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Last {selectedTimeRange} days</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Active Members</h3>
              <div className="space-y-3">
                {activitySummary?.userActivity.slice(0, 5).map((user, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <span className="text-gray-900 dark:text-gray-600 font-medium text-sm">
                          {user.first_name[0]}{user.last_name[0]}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900 dark:text-gray-600">{user.activity_count}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">activities</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Login Activity</h3>
              <div className="space-y-3">
                {teamMembers
                  .filter(member => member.last_login_at)
                  .sort((a, b) => new Date(b.last_login_at!).getTime() - new Date(a.last_login_at!).getTime())
                  .slice(0, 5)
                  .map(member => (
                    <div key={member.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                          <span className="text-green-600 dark:text-green-400 font-medium text-sm">
                            {member.first_name[0]}{member.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {member.first_name} {member.last_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{member.department_name}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(member.last_login_at!).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Tab */}
      {activeView === 'activity' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Daily Activity Trend</h3>
            <div className="h-64">
              <Line data={dailyActivityData} options={{...chartOptions, maintainAspectRatio: false}} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Activity by Type</h3>
            <div className="h-64">
              <Doughnut data={activityByActionData} options={{...chartOptions, maintainAspectRatio: false}} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Most Active Users</h3>
            <div className="h-64">
              <Bar data={userActivityData} options={{...chartOptions, maintainAspectRatio: false}} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Activity Summary</h3>
            <div className="space-y-4">
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-600">{activitySummary?.totalActivities || 0}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Total activities in last {selectedTimeRange} days</div>
              </div>
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {teamStats?.activeCollaborators && activitySummary?.totalActivities
                    ? Math.round(activitySummary.totalActivities / teamStats.activeCollaborators)
                    : 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Average activities per member</div>
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900 dark:text-gray-600">
                  {activitySummary?.actionCounts[0]?.action.replace('_', ' ').toUpperCase() || 'N/A'}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Most common action ({activitySummary?.actionCounts[0]?.count || 0} times)
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Structure Tab */}
      {activeView === 'team' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Team by Department</h3>
              <div className="h-64">
                <Doughnut data={departmentDistributionData} options={{...chartOptions, maintainAspectRatio: false}} />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Department Leaders</h3>
              <div className="space-y-4">
                {departments.map(dept => (
                  <div key={dept.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: dept.color }}
                      ></div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{dept.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{dept.memberCount} members</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {dept.lead_first_name && dept.lead_last_name 
                          ? `${dept.lead_first_name} ${dept.lead_last_name}`
                          : 'No leader assigned'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
